import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Upload } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ownerApi, errText } from "@/lib/ownerApi";

const ImportResult = ({ r }) => (
  <div className="rounded-xl border border-stone-200 bg-white p-3 text-sm space-y-1" data-testid="owner-groups-import-result">
    <p><b className="text-emerald-800">{r.matched.length}</b> matched · <b className="text-amber-700">{r.ambiguous.length}</b> same name (pick below) · <b className="text-red-700">{r.unmatched.length}</b> not found</p>
    {r.unmatched.length > 0 && <p className="text-xs text-red-700">Not found: {r.unmatched.map((u) => `${u.name} (${u.group})`).join(", ")}</p>}
    {r.ambiguous.length > 0 && <p className="text-xs text-amber-700">Same name, set by hand: {r.ambiguous.map((u) => `${u.name} → ${u.group} [${u.candidates.map((c) => c.roll_number).join(" / ")}]`).join(", ")}</p>}
  </div>
);

export default function Groups() {
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const load = useCallback(() => ownerApi.get("/students").then(({ data }) => setD(data)).catch((e) => toast.error(errText(e))), []);
  useEffect(() => { load(); }, [load]);
  const rows = useMemo(() => (d?.students || []).filter((s) => `${s.name} ${s.roll_number} ${s.group} ${s.student_class}`.toLowerCase().includes(q.toLowerCase())), [d, q]);
  const setGroup = async (s, g) => {
    try { await ownerApi.put(`/students/${s.id}/group`, { group: g === "auto" ? null : g }); toast.success(`${s.name}: ${g === "auto" ? s.derived : g}`); load(); }
    catch (e) { toast.error(errText(e)); }
  };
  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { const { data } = await ownerApi.post("/students/import-groups", fd); setRes(data); load(); } catch (er) { toast.error(errText(er)); }
    e.target.value = "";
  };
  if (!d) return <Loader2 className="h-6 w-6 animate-spin text-emerald-800 mx-auto" />;
  return (
    <div className="space-y-4" data-testid="owner-groups">
      <p className="text-sm text-stone-600">Each student's fees count toward one salary group. Std 1–10, Nur–Sr KG and College are set automatically from the standard. School, Private, Mix Batch and College are set from your Excel sheets or by hand.</p>
      <div className="flex flex-wrap items-center gap-3">
        <label className="inline-flex items-center gap-2 rounded-md border border-stone-300 bg-white px-3 h-9 text-sm font-medium cursor-pointer hover:bg-stone-50" data-testid="owner-groups-import">
          <Upload className="h-4 w-4" /> Import from fees Excel<input type="file" accept=".xlsx" className="hidden" onChange={upload} data-testid="owner-groups-import-input" />
        </label>
        <Input placeholder="Search name, roll, group" value={q} onChange={(e) => setQ(e.target.value)} className="w-64" data-testid="owner-groups-search" />
      </div>
      {res && <ImportResult r={res} />}
      <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white">
        <table className="min-w-full text-sm" data-testid="owner-groups-students">
          <thead className="bg-stone-100 text-stone-600"><tr>{["Student", "Roll", "Standard", "Salary group"].map((h) => <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-stone-100">
            {rows.map((s) => (
              <tr key={s.id} data-testid={`owner-group-row-${s.roll_number}`}>
                <td className="px-3 py-1.5">{s.name}</td><td className="px-3">{s.roll_number}</td><td className="px-3">{s.student_class}</td>
                <td className="px-3">
                  <select className="h-8 rounded-md border border-input bg-white px-2 text-sm" value={s.salary_group || "auto"} onChange={(e) => setGroup(s, e.target.value)} data-testid={`owner-group-select-${s.roll_number}`}>
                    <option value="auto">{`Auto: ${s.derived}`}</option>
                    {d.groups.filter((g) => g !== "Unassigned").map((g) => <option key={g} value={g}>{g}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
